import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Seo, JsonLd } from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import { siteInfo } from "@/mocks/articles";

const principles = [
  {
    icon: "ri-file-search-line",
    title: "先找出处，再谈结论",
    text: "每条传言都必须追溯到最早发布的版本，区分原始截图与二次转述，避免把改写过的标题当作事实。",
  },
  {
    icon: "ri-links-line",
    title: "证据可核对",
    text: "结论区逐条列出依据，包括报告编号、监控时段、订单记录等，读者可以按同一条路径自行复核。",
  },
  {
    icon: "ri-refresh-line",
    title: "错了就公开更正",
    text: "当新的证据推翻原有结论时，我们会更新鉴定结果，并保留完整时间线，而不是悄悄替换旧内容。",
  },
  {
    icon: "ri-user-heart-line",
    title: "不放大情绪",
    text: "我们关注的是事实本身，不追逐猎奇标题，也不渲染当事人的隐私与情绪化细节。",
  },
];

const testimonials = [
  {
    name: "林女士",
    role: "长期读者",
    text: "家里长辈总在群里转各种提醒，我把这里的鉴定发过去，他们现在会先问一句“这个核实了吗”。",
  },
  {
    name: "周先生",
    role: "社区工作者",
    text: "处理邻里传言时经常参考这里的结论，时间线做得很清楚，容易给居民讲明白来龙去脉。",
  },
  {
    name: "阿澄",
    role: "内容创作者",
    text: "最认可的一点是它会把后续回应补上，很多平台只报开头，这里是唯一能看到结尾的地方。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 17爆料网",
      url: "https://dahuaqi.cn/about",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "17爆料网",
      url: "https://dahuaqi.cn/",
      email: siteInfo.email,
      description: siteInfo.intro,
      foundingDate: siteInfo.since,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://dahuaqi.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://dahuaqi.cn/about" },
      ],
    },
  ];

  return (
    <Layout>
      <Seo
        title="关于我们 - 17爆料网真假鉴定专区"
        description="了解17爆料网的品牌定位、内容原则与核实流程。我们专注网络传言的辟谣、反转与后续回应追踪，坚持证据可核对、错误公开更正。"
        keywords="17爆料网,关于17爆料网,真假鉴定,内容原则,核实流程,后续回应"
        path="/about"
      />
      <JsonLd data={jsonLd} />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-800 text-foreground-950 md:text-4xl">关于 17爆料网</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">{siteInfo.intro}</p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl space-y-16 px-4 py-14 md:space-y-24 md:px-8 md:py-20">
        <section className="grid items-center gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Calm%20editorial%20desk%20scene%20with%20open%20notebooks%20a%20magnifying%20glass%20and%20printed%20screenshots%20laid%20out%20for%20verification%2C%20warm%20amber%20morning%20light%2C%20muted%20cream%20and%20brown%20palette%2C%20documentary%20still%20life&width=900&height=650&seq=about-17-team&orientation=landscape"
              alt="用于核实工作的笔记、放大镜与打印材料"
              className="h-full w-full object-top"
            />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-500 text-secondary-800">
              <i className="ri-book-open-line"></i>
              品牌介绍
            </span>
            <h2 className="mt-4 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
              让“爆料”有始有终
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              17爆料网成立于 {siteInfo.since} 年，最初的出发点很简单：网络上的爆料常常只留下一个高赞标题，却没人关心它最后是否被证实。我们把散落各处的澄清、通报与后续回应收集起来，按时间顺序整理成一条完整的链路。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              如今，真假鉴定专区已经成为站内最核心的部分。它不提供立场，只提供依据；不追求最快，只追求可核对。我们希望读者看完之后，能自己判断哪些料站得住，哪些已经翻车。
            </p>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">我们的内容原则</h2>
          <p className="mt-2 max-w-3xl text-sm text-foreground-600">
            这四条原则决定了每条鉴定记录的写法，也是我们判断“能不能下结论”的标准。
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {principles.map((p, idx) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <span className="font-label text-sm text-foreground-400">0{idx + 1}</span>
                </div>
                <h3 className="mt-4 font-heading text-base font-600 text-foreground-950 md:text-lg">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-6 rounded-lg border border-background-200 bg-background-100 p-6 md:grid-cols-2 md:p-10">
          <div>
            <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">网站定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              17爆料网不是一个八卦集散地，而是一个核实工具。我们把“真假鉴定”“反转追踪”“打脸现场”分成不同入口：前者给你结论，后两者给你过程，方便不同需求的读者快速找到需要的部分。
            </p>
          </div>
          <ul className="space-y-4">
            {[
              { k: "真假鉴定", v: "证据链完整、结论明确的条目，标注已证实或已辟谣。" },
              { k: "反转追踪", v: "事件走向发生变化的条目，保留前后两版说法。" },
              { k: "打脸现场", v: "当事人主动放出的说法被证据直接推翻的案例。" },
            ].map((row) => (
              <li key={row.k} className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 p-4">
                <i className="ri-checkbox-circle-fill mt-0.5 text-secondary-500"></i>
                <div>
                  <span className="text-sm font-600 text-foreground-950">{row.k}</span>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{row.v}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">读者怎么说</h2>
          <p className="mt-2 text-sm text-foreground-600">来自读者的反馈，是我们持续更新结论的动力。</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3">
                  <span className="text-sm font-600 text-foreground-950">{t.name}</span>
                  <span className="mt-0.5 block text-xs text-foreground-500">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="scroll-mt-24">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <h2 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
              无论是线索投稿、内容更正还是合作咨询，都可以通过下面的方式找到我们。核实组通常会在两个工作日内回复。
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-md border border-background-200 bg-background-100 p-5">
                <i className="ri-mail-send-line text-xl text-primary-600"></i>
                <h3 className="mt-3 text-sm font-600 text-foreground-950">线索投稿</h3>
                <p className="mt-1 text-sm text-foreground-600">{siteInfo.submitEmail}</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 p-5">
                <i className="ri-customer-service-2-line text-xl text-primary-600"></i>
                <h3 className="mt-3 text-sm font-600 text-foreground-950">合作与更正</h3>
                <p className="mt-1 text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 p-5">
                <i className="ri-time-line text-xl text-primary-600"></i>
                <h3 className="mt-3 text-sm font-600 text-foreground-950">响应时间</h3>
                <p className="mt-1 text-sm text-foreground-600">工作日 09:00 - 18:00</p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/#submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                在线提交线索
              </Link>
              <Link
                to="/verify"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-500 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                浏览鉴定专区
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}